import { useLatest, useTimeout } from '@solinkcorp/solink-components';
import { useWindowVisibility } from '@solinkcorp/utils/hooks';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { type Dispatch, useEffect, useMemo, useState } from 'react';
import * as auth0 from '../auth0';
import { LOGOUT_REASON, type LogoutReason } from '../authConstants';
import { type JWTUser } from '../callHome/callHomeAuthTypes';
import * as authActions from '../state/authenticationActions';
import { type AuthenticationState } from '../state/authenticationReducer';
import { type ImpersonatorSession, type LogoutHandler, type UserSession } from '../types';

const SHOW_BAD_SESSION_DIALOG_TIME_MS = 3000;

/**
 * This defines how long before the user's token expires we will try to refresh
 * the session.
 */
const TIME_TO_REFRESH_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Periodically refreshes the user's session.
 *
 * This also handles the case where a user logs out or switches who they are
 * impersonating in some other tab.  If this happens, we take care of logging
 * the user out, or of fixing up their session for an impersonator.  This will
 * return `showBadSessionDialog: true` if the user's session needs to be fixed.
 */
export function useRefreshUser(
  authDispatch: Dispatch<authActions.AuthAction>,
  state: AuthenticationState,
  logoutHandlers: { current: LogoutHandler }[],
): { showBadSessionDialog: boolean } {
  const windowVisible = useWindowVisibility();
  const currentState = useLatest(state);

  const [showBadSessionDialog, setShowBadSessionDialog] = useState(false);

  useRefreshSession(authDispatch, state, state.userSession, logoutHandlers);
  useRefreshSession(authDispatch, state, state.impersonator, logoutHandlers);

  // Whenever the user comes back to the window, verify the token hasn't expired.
  // Also, if the user is impersonating someone, verify that they didn't change
  // who they were impersonating in some other tab.
  useEffect(() => {
    const session = currentState.current.userSession;
    if (!session || !windowVisible) {
      return;
    }

    // When the current browser tab was out of view and is brought back to view
    // decoding the jwt token in the browser cookie to get current valid userId
    // check if our session's userId is the same if not, it's invalid and we
    // should logout to prevent confusion.
    const validJwtToken = Cookies.get('jwt');
    if (!validJwtToken) {
      // The `jwt` cookie is gone.  There is a situation where the token expired
      // while the tab was in the background.  Browsers throttle and freeze
      // background timers, so the refresh timer in `useRefreshSession` can miss
      // its window, and the browser then deletes the expired cookie.  Do not log
      // the user out for this.  Refresh the session first, which silently
      // re-authenticates and re-sets the cookie.  `refreshSession` re-impersonates
      // when needed, so the impersonated user's cookie is re-set too.  Only log
      // out if the refresh fails.  The returned function cancels an in-flight
      // attempt if the tab is hidden again before it resolves.
      return refreshSession(authDispatch, session, logoutHandlers, {
        reason: LOGOUT_REASON.WINDOW_VISIBILITY,
        source: 'useRefreshUser# - invalidJWT',
        onBeforeLogout: () => setShowBadSessionDialog(true),
      });
    }

    // Get the userID from the cookie.
    const { sub: validUserId } = jwtDecode(validJwtToken) as JWTUser;
    const userId = session.jwtUser.sub;

    if (userId !== validUserId) {
      setShowBadSessionDialog(true);
      setTimeout(() => {
        if (currentState.current.impersonator) {
          // Re-impersonate the original user.
          authActions.impersonateAction(currentState.current.impersonator.accessToken, userId).then(
            () => {
              // Force a reload.
              window.location.reload();
            },
            (err) => {
              console.error(`Error trying to refresh impersonated user`, err);
              auth0.logout(session, logoutHandlers, LOGOUT_REASON.WINDOW_VISIBILITY, undefined, {
                source: 'useRefreshUser# - refreshImpersonationFailed',
                message: JSON.stringify(err),
              });
            },
          );
        } else {
          auth0.logout(session, logoutHandlers, LOGOUT_REASON.WINDOW_VISIBILITY, undefined, {
            source: 'useRefreshUser# - invalidUserId',
            message: 'Invalid userId due to multiple logins',
          });
        }
      }, SHOW_BAD_SESSION_DIALOG_TIME_MS);
      return;
    }
  }, [windowVisible, currentState, logoutHandlers, authDispatch]);

  return { showBadSessionDialog };
}

/**
 * Set a timeout to refresh the user or impersonator session when it expires.
 */
function useRefreshSession(
  authDispatch: Dispatch<authActions.AuthAction>,
  state: AuthenticationState,
  session: UserSession | ImpersonatorSession | undefined,
  logoutHandlers: { current: LogoutHandler }[],
) {
  const userExpiryTime = useMemo(() => sessionExpiryTime(session), [session]);

  // Work out how long to set the timer for.
  let timeUntilRefresh: number | undefined;
  if (!session) {
    timeUntilRefresh = undefined;
  } else if (session.type === 'guest') {
    // Guests can't be refreshed - their expiry is fixed.
    timeUntilRefresh = undefined;
  } else if (userExpiryTime) {
    // Refresh the session 10 minutes before it expires, unless the session is
    // short lived, in which case refresh it 1/10th of it's lifetime before it expires.
    const sessionDuration = (session.jwtUser.exp - session.jwtUser.iat) * 1000;
    const timeBeforeExpiry = Math.min(TIME_TO_REFRESH_MS, sessionDuration / 10);
    timeUntilRefresh = Math.max(userExpiryTime - Date.now() - timeBeforeExpiry, 0);
  }

  useTimeout(() => {
    if (session) {
      refreshSession(authDispatch, session, logoutHandlers);
    }
  }, timeUntilRefresh);
}

/**
 * Returns the time (in milliseconds since the epoch) when the user's session expires.
 */
function sessionExpiryTime(
  session: UserSession | ImpersonatorSession | undefined,
): number | undefined {
  if (!session) {
    return undefined;
  }

  let expiryTime = session.jwtUser.exp * 1000;

  if (session.type !== 'impersonator') {
    // Only check the firebase token for a call-home user.  We don't do this for
    // impersonators, because we don't actually use their firebase token for anything.

    const firebaseTokenPayload = session.firebaseToken
      ? (jwtDecode(session.firebaseToken) as { exp: number } | undefined)
      : undefined;
    if (firebaseTokenPayload) {
      const firebaseTokenExpiry = firebaseTokenPayload.exp * 1000;
      if (firebaseTokenExpiry < expiryTime) {
        // The firebase token expires before the JWT token - need to refresh when
        // the firebase token expires.
        expiryTime = firebaseTokenExpiry;
      }
    }
  }

  return expiryTime;
}

/**
 * Refresh a session.
 *
 * If the session is impersonated, this re-impersonates the target user, so the
 * impersonated user's cookies are re-set.  Otherwise it refreshes the current
 * user.  If the refresh fails, the user is logged out.
 *
 * Returns a function that cancels the pending refresh.  Cancelling stops the
 * dispatch and the logout, but not the underlying re-authentication, so any
 * cookie the refresh re-sets is still restored.
 *
 * @param [options.reason] - The logout reason to record if the refresh fails.
 * @param [options.source] - The analytics source to record if the refresh fails.
 * @param [options.onBeforeLogout] - Run just before the failure logout, for
 *   example to show the bad-session dialog.
 */
function refreshSession(
  authDispatch: Dispatch<authActions.AuthAction>,
  session: UserSession | ImpersonatorSession,
  logoutHandlers: { current: LogoutHandler }[],
  options?: { reason?: LogoutReason; source?: string; onBeforeLogout?: () => void },
): () => void {
  let cancelled = false;
  const reason = options?.reason ?? LOGOUT_REASON.REFRESHING_SESSION;
  const source = options?.source ?? 'useRefreshUser# - refreshSession';

  Promise.resolve()
    .then(async () => {
      const action = await authActions.refreshSessionAction();
      if (!cancelled) {
        authDispatch(action);
      }
    })
    .catch((err) => {
      if (cancelled) {
        return;
      }
      console.error(`[AUTH]: Unable to refresh ${session.type} session`, err);
      options?.onBeforeLogout?.();
      auth0.logout(
        session.type === 'impersonator' ? undefined : session,
        logoutHandlers,
        reason,
        undefined,
        {
          source,
          message: JSON.stringify(err),
        },
      );
    });

  return () => {
    cancelled = true;
  };
}
